import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  MapPin,
  Wifi,
  Clock,
  Radio,
  RefreshCw,
} from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useFraudIntelligenceOverview,
  useDriverRiskScores,
  useFlaggedBookings,
  useDriverIncidents,
  useConfirmDriverIncident,
  useClearDriverIncident,
} from '../hooks/useFraud';
import type { DriverFraudStatus } from '../api/fraud.api';

// ─── Small helpers ────────────────────────────────────────────────────────────

function IncidentTypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; className: string }> = {
    delivery_abandoned: { label: 'Abandoned', className: 'bg-red-100 text-red-700' },
    sla_breach: { label: 'SLA Breach', className: 'bg-orange-100 text-orange-700' },
    gps_ghost: { label: 'GPS Ghost', className: 'bg-purple-100 text-purple-700' },
  };
  const { label, className } = map[type] ?? { label: type, className: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${className}`}>{label}</span>;
}

function IncidentStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'warning' | 'success' }> = {
    suspected: { label: 'Suspected', variant: 'warning' },
    confirmed: { label: 'Confirmed', variant: 'destructive' },
    cleared: { label: 'Cleared', variant: 'success' },
  };
  const { label, variant } = map[status] ?? { label: status, variant: 'default' };
  return <Badge variant={variant}>{label}</Badge>;
}

function DetectedByBadge({ by }: { by: string }) {
  const map: Record<string, { label: string; icon: typeof Wifi }> = {
    socket_disconnect: { label: 'Disconnect', icon: Wifi },
    sla_deadline: { label: 'SLA Deadline', icon: Clock },
    gps_cron: { label: 'GPS Cron', icon: Radio },
  };
  const { label, icon: Icon } = map[by] ?? { label: by, icon: AlertTriangle };
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function RiskBar({ score }: { score: number }) {
  const color = score >= 70 ? 'bg-red-500' : score >= 40 ? 'bg-orange-400' : 'bg-yellow-400';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="min-w-[2rem] text-right text-xs font-semibold">{score}</span>
    </div>
  );
}

// ─── Notes modal ─────────────────────────────────────────────────────────────

function NotesDialog({
  title,
  action,
  onConfirm,
  onClose,
  loading,
}: {
  title: string;
  action: 'confirm' | 'clear';
  onConfirm: (notes: string) => void;
  onClose: () => void;
  loading: boolean;
}) {
  const [notes, setNotes] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-base font-semibold">{title}</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {action === 'confirm'
            ? 'This will ban the driver and cancel the booking. This action cannot be undone.'
            : 'This will unfreeze the driver\'s wallet and unflag the booking.'}
        </p>
        <textarea
          className="mb-4 w-full rounded border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          rows={3}
          placeholder="Admin notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant={action === 'confirm' ? 'destructive' : 'default'}
            onClick={() => onConfirm(notes)}
            disabled={loading}
          >
            {loading ? <Spinner className="mr-2 h-4 w-4" /> : null}
            {action === 'confirm' ? 'Confirm Fraud' : 'Clear Incident'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Overview stat cards ──────────────────────────────────────────────────────

function OverviewCards() {
  const { data, isLoading } = useFraudIntelligenceOverview();

  if (isLoading) return <div className="flex h-32 items-center justify-center"><Spinner /></div>;

  const stats = [
    { label: 'Suspected', value: data?.total_suspected ?? 0, icon: AlertTriangle, color: 'text-yellow-500' },
    { label: 'Confirmed Fraud', value: data?.total_confirmed ?? 0, icon: XCircle, color: 'text-red-500' },
    { label: 'Cleared', value: data?.total_cleared ?? 0, icon: CheckCircle, color: 'text-green-500' },
    { label: 'Flagged Active', value: data?.flagged_active_deliveries ?? 0, icon: MapPin, color: 'text-orange-500' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {stats.map(({ label, value, icon: Icon, color }) => (
        <Card key={label}>
          <CardContent className="flex items-center gap-4 p-5">
            <Icon className={`h-8 w-8 ${color}`} />
            <div>
              <p className="text-2xl font-bold">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Incident queue ───────────────────────────────────────────────────────────

function IncidentQueue() {
  const [status, setStatus] = useState<DriverFraudStatus | 'all'>('suspected');
  const [dialog, setDialog] = useState<{ id: string; action: 'confirm' | 'clear' } | null>(null);

  const { data, isLoading } = useDriverIncidents({
    status: status === 'all' ? undefined : status,
    limit: 30,
  });

  const confirm = useConfirmDriverIncident();
  const clear = useClearDriverIncident();

  const handleAction = (notes: string) => {
    if (!dialog) return;
    const mutation = dialog.action === 'confirm' ? confirm : clear;
    mutation.mutate({ id: dialog.id, notes: notes || undefined }, { onSuccess: () => setDialog(null) });
  };

  const statusOptions = [
    { value: 'suspected', label: 'Suspected' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'cleared', label: 'Cleared' },
    { value: 'all', label: 'All' },
  ];

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="font-semibold">Driver Fraud Incidents</h2>
          <Select
            options={statusOptions}
            value={status}
            onChange={(v) => setStatus(v as DriverFraudStatus | 'all')}
            className="w-36"
          />
        </div>
        {isLoading ? (
          <div className="flex h-48 items-center justify-center"><Spinner /></div>
        ) : !data?.items?.length ? (
          <EmptyState title="No incidents found" description="No driver fraud incidents match the current filter." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Detected By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Detected At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((incident) => (
                <TableRow key={incident.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{incident.driver?.name ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">{incident.driver?.phone ?? incident.driver_id}</p>
                    </div>
                  </TableCell>
                  <TableCell><IncidentTypeBadge type={incident.type} /></TableCell>
                  <TableCell><DetectedByBadge by={incident.detected_by} /></TableCell>
                  <TableCell><IncidentStatusBadge status={incident.status} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(incident.detected_at)}</TableCell>
                  <TableCell>
                    {incident.status === 'suspected' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setDialog({ id: incident.id, action: 'confirm' })}
                        >
                          Confirm Fraud
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDialog({ id: incident.id, action: 'clear' })}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {dialog && (
        <NotesDialog
          title={dialog.action === 'confirm' ? 'Confirm Driver Fraud' : 'Clear Incident (False Positive)'}
          action={dialog.action}
          onConfirm={handleAction}
          onClose={() => setDialog(null)}
          loading={confirm.isPending || clear.isPending}
        />
      )}
    </Card>
  );
}

// ─── Flagged active deliveries ────────────────────────────────────────────────

function FlaggedDeliveries() {
  const { data, isLoading, refetch } = useFlaggedBookings();

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="font-semibold">Flagged Active Deliveries</h2>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Refresh
          </Button>
        </div>
        {isLoading ? (
          <div className="flex h-48 items-center justify-center"><Spinner /></div>
        ) : !data?.length ? (
          <EmptyState title="No flagged deliveries" description="No active deliveries are currently flagged." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Route</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Flag Reason</TableHead>
                <TableHead>Flagged At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>
                    <div className="max-w-xs">
                      <p className="truncate text-xs font-medium">{booking.pickup_address}</p>
                      <p className="truncate text-xs text-muted-foreground">→ {booking.drop_address}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{booking.driver?.name ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">{booking.driver?.phone}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{booking.customer?.name ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">{booking.customer?.phone}</p>
                  </TableCell>
                  <TableCell>
                    <DetectedByBadge by={booking.flag_reason} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(booking.flagged_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Risk leaderboard ─────────────────────────────────────────────────────────

function RiskLeaderboard() {
  const { data, isLoading } = useDriverRiskScores(20);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="border-b px-5 py-4">
          <h2 className="font-semibold">Driver Risk Leaderboard</h2>
          <p className="text-xs text-muted-foreground">Risk score 0–100 based on fraud incidents, disconnects, SLA breaches</p>
        </div>
        {isLoading ? (
          <div className="flex h-48 items-center justify-center"><Spinner /></div>
        ) : !data?.length ? (
          <EmptyState title="No data" description="No drivers with fraud incidents found." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Risk Score</TableHead>
                <TableHead>Confirmed</TableHead>
                <TableHead>Suspected</TableHead>
                <TableHead>SLA Breaches</TableHead>
                <TableHead>GPS Ghosts</TableHead>
                <TableHead>Trips</TableHead>
                <TableHead>Wallet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, i) => (
                <TableRow key={row.driver_id}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell>
                    <p className="font-medium">{row.driver_name}</p>
                    <p className="text-xs text-muted-foreground">{row.driver_phone}</p>
                  </TableCell>
                  <TableCell><RiskBar score={row.risk_score} /></TableCell>
                  <TableCell>
                    <span className={`font-semibold ${row.breakdown.confirmed_incidents > 0 ? 'text-red-600' : ''}`}>
                      {row.breakdown.confirmed_incidents}
                    </span>
                  </TableCell>
                  <TableCell className="text-yellow-600">{row.breakdown.suspected_incidents}</TableCell>
                  <TableCell>{row.breakdown.sla_breaches}</TableCell>
                  <TableCell>{row.breakdown.gps_ghost_events}</TableCell>
                  <TableCell className="text-muted-foreground">{row.total_trips}</TableCell>
                  <TableCell>
                    {row.wallet_frozen ? (
                      <Badge variant="destructive">Frozen</Badge>
                    ) : (
                      <Badge variant="secondary">Active</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FraudIntelligencePage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Fraud Intelligence"
        description="Real-time detection of abandoned deliveries and driver fraud"
      />

      <OverviewCards />

      <div className="grid gap-6 xl:grid-cols-2">
        <FlaggedDeliveries />
        <RiskLeaderboard />
      </div>

      <IncidentQueue />
    </div>
  );
}
